import React, { useState, useRef, useEffect } from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface LocationAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  label?: string;
  className?: string;
  inputClassName?: string;
  onKeyPress?: (e: React.KeyboardEvent) => void;
}

export function LocationAutocomplete({
  value,
  onChange,
  options,
  placeholder = "Selecione...",
  label,
  className,
  inputClassName,
  onKeyPress,
}: LocationAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(inputValue.toLowerCase())
  );

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setInputValue(selectedValue);
    setOpen(false);
  };

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    onChange(newValue);
    if (!open && newValue.length > 0) {
      setOpen(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
    }
    onKeyPress?.(e);
  };

  return (
    <div className={cn("relative", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="w-full cursor-text" onClick={() => inputRef.current?.focus()}>
            {label && (
              <label className="text-xs md:text-sm font-semibold text-neutral-black/80 whitespace-nowrap pointer-events-none">
                {label}
              </label>
            )}
            <div className="flex items-center">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
                onFocus={() => setOpen(true)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                className={cn(
                  "w-full font-normal text-neutral-black bg-transparent border-none outline-none placeholder:text-gray-400/70",
                  "text-base md:text-base lg:text-lg",
                  "py-1 focus:outline-none focus:ring-0",
                  inputClassName
                )}
              />
              <ChevronDown className={cn(
                "h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200",
                open && "rotate-180"
              )} />
            </div>
          </div>
        </PopoverTrigger>
        <PopoverContent 
          className="w-[--radix-popover-trigger-width] p-0 bg-white border border-gray-200 shadow-lg z-[100]" 
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command shouldFilter={false}>
            <CommandList className="max-h-[200px] overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <CommandEmpty className="py-3 text-center text-sm text-gray-500">
                  Nenhum resultado encontrado
                </CommandEmpty>
              ) : (
                <CommandGroup>
                  {filteredOptions.map((option) => (
                    <CommandItem
                      key={option}
                      value={option}
                      onSelect={() => handleSelect(option)}
                      className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-100"
                    >
                      <span>{option}</span>
                      {value === option && (
                        <Check className="h-4 w-4 text-brand-gold" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default LocationAutocomplete;
